/**
 * Menu Renderer Component
 *
 * Renders dynamic menu items with icon support.
 * Works with both CMS-managed menus and config-based menus.
 */
import { Link } from 'react-router-dom'
import * as LucideIcons from 'lucide-react'
import { ExternalLink, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DynamicMenuItem } from '@/client/hooks/useDynamicMenu'

// =============================================================================
// Types
// =============================================================================

export interface MenuRendererProps {
  items: DynamicMenuItem[]
  variant?: 'header' | 'footer' | 'sidebar' | 'mobile'
  isActive?: (item: DynamicMenuItem) => boolean
  onItemClick?: () => void
  className?: string
}

export interface MenuItemProps {
  item: DynamicMenuItem
  variant: 'header' | 'footer' | 'sidebar' | 'mobile'
  isActive?: boolean
  onItemClick?: () => void
}

// =============================================================================
// Icon Utility
// =============================================================================

/**
 * Get Lucide icon component from string name
 */
function getIcon(name: string | undefined): LucideIcons.LucideIcon | null {
  if (!name) return null
  const Icon = LucideIcons[name as keyof typeof LucideIcons]
  if (typeof Icon === 'function') {
    return Icon as LucideIcons.LucideIcon
  }
  return null
}

/**
 * Render dynamic icon from name
 */
function DynamicIcon({
  name,
  className,
}: {
  name: string | undefined
  className?: string
}) {
  const Icon = getIcon(name)
  if (!Icon) return null
  return <Icon className={className} />
}

// =============================================================================
// Header Variant
// =============================================================================

function HeaderMenuItem({ item, isActive, onItemClick }: MenuItemProps) {
  const hasChildren = item.children && item.children.length > 0
  const Icon = getIcon(item.icon)

  // Render link or external link
  const renderLink = (child: DynamicMenuItem) => {
    const linkClasses = cn(
      'block px-3 py-2 text-sm rounded-md transition-colors',
      'hover:bg-accent hover:text-accent-foreground'
    )

    if (child.external) {
      return (
        <a
          href={child.href}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(linkClasses, 'inline-flex items-center gap-1')}
          onClick={onItemClick}
        >
          {child.label}
          <ExternalLink className="h-3 w-3 opacity-60" />
        </a>
      )
    }

    return (
      <Link to={child.href} className={linkClasses} onClick={onItemClick}>
        {child.label}
      </Link>
    )
  }

  // Simple link without children
  if (!hasChildren && item.href) {
    return (
      <Link
        to={item.href}
        className={cn(
          'text-sm font-medium transition-colors hover:text-primary inline-flex items-center gap-1.5',
          isActive ? 'text-foreground' : 'text-muted-foreground'
        )}
        onClick={onItemClick}
      >
        {Icon && <Icon className="h-4 w-4" />}
        {item.label}
        {item.external && <ExternalLink className="h-3 w-3 opacity-60" />}
      </Link>
    )
  }

  // Dropdown with children (using CSS :hover for now)
  return (
    <div className="relative group">
      <button
        className={cn(
          'text-sm font-medium transition-colors hover:text-primary inline-flex items-center gap-1.5',
          isActive ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {Icon && <Icon className="h-4 w-4" />}
        {item.label}
        <ChevronDown className="h-3 w-3 opacity-60 transition-transform group-hover:rotate-180" />
      </button>

      <div
        className={cn(
          'absolute top-full left-0 pt-2 z-50',
          'opacity-0 invisible group-hover:opacity-100 group-hover:visible',
          'transition-all duration-150'
        )}
      >
        <div className="bg-popover border rounded-lg shadow-lg p-1 min-w-[180px]">
          {item.children?.map((child) => (
            <div key={child.id}>{renderLink(child)}</div>
          ))}
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Footer Variant
// =============================================================================

function FooterColumnItem({ item }: { item: DynamicMenuItem }) {
  const Icon = getIcon(item.icon)

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
      >
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {item.label}
        <ExternalLink className="h-3 w-3 opacity-60" />
      </a>
    )
  }

  return (
    <Link
      to={item.href || '#'}
      className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {item.label}
    </Link>
  )
}

function FooterColumn({ item }: { item: DynamicMenuItem }) {
  const Icon = getIcon(item.icon)
  const hasChildren = item.children && item.children.length > 0

  // If no children, render as a single link
  if (!hasChildren && item.href) {
    return <FooterColumnItem item={item} />
  }

  // Render as a column with heading
  return (
    <div>
      <h3 className="font-semibold mb-4 flex items-center gap-1.5">
        {Icon && <Icon className="h-4 w-4" />}
        {item.label}
      </h3>
      <ul className="space-y-3">
        {item.children?.map((child) => (
          <li key={child.id}>
            <FooterColumnItem item={child} />
          </li>
        ))}
      </ul>
    </div>
  )
}

// =============================================================================
// Sidebar Variant
// =============================================================================

function SidebarMenuItem({
  item,
  isActive,
  onItemClick,
  depth = 0,
}: MenuItemProps & { depth?: number }) {
  const Icon = getIcon(item.icon)
  const hasChildren = item.children && item.children.length > 0

  // Simple link
  if (!hasChildren && item.href) {
    return (
      <Link
        to={item.href}
        onClick={onItemClick}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          isActive && 'bg-accent text-accent-foreground font-medium',
          depth > 0 && 'ml-4'
        )}
      >
        {Icon ? (
          <Icon className="h-4 w-4 text-muted-foreground" />
        ) : (
          depth === 0 && <div className="w-4" />
        )}
        <span className="flex-1">{item.label}</span>
        {item.external && <ExternalLink className="h-3 w-3 opacity-60" />}
      </Link>
    )
  }

  // Group with children
  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground',
          depth > 0 && 'ml-4'
        )}
      >
        {Icon && <Icon className="h-4 w-4" />}
        <span>{item.label}</span>
      </div>
      <div className="space-y-1">
        {item.children?.map((child) => (
          <SidebarMenuItem
            key={child.id}
            item={child}
            variant="sidebar"
            isActive={false}
            onItemClick={onItemClick}
            depth={depth + 1}
          />
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// Mobile Variant
// =============================================================================

function MobileMenuItem({ item, isActive, onItemClick }: MenuItemProps) {
  const Icon = getIcon(item.icon)
  const hasChildren = item.children && item.children.length > 0

  // Simple link
  if (!hasChildren && item.href) {
    return (
      <Link
        to={item.href}
        onClick={onItemClick}
        className={cn(
          'flex items-center gap-3 text-lg font-medium transition-colors hover:text-primary',
          isActive ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {Icon && <Icon className="h-5 w-5" />}
        {item.label}
        {item.external && <ExternalLink className="h-4 w-4 opacity-60" />}
      </Link>
    )
  }

  // Group with children
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-lg font-medium text-muted-foreground">
        {Icon && <Icon className="h-5 w-5" />}
        {item.label}
      </div>
      <div className="pl-8 space-y-2">
        {item.children?.map((child) => (
          <MobileMenuItem
            key={child.id}
            item={child}
            variant="mobile"
            isActive={false}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function MenuRenderer({
  items,
  variant = 'header',
  isActive,
  onItemClick,
  className,
}: MenuRendererProps) {
  if (variant === 'header') {
    return (
      <nav className={cn('flex items-center gap-6', className)}>
        {items.map((item) => (
          <HeaderMenuItem
            key={item.id}
            item={item}
            variant="header"
            isActive={isActive?.(item)}
            onItemClick={onItemClick}
          />
        ))}
      </nav>
    )
  }

  if (variant === 'footer') {
    return (
      <div className={className}>
        {items.map((item) => (
          <FooterColumn key={item.id} item={item} />
        ))}
      </div>
    )
  }

  if (variant === 'sidebar') {
    return (
      <nav className={cn('space-y-1', className)}>
        {items.map((item) => (
          <SidebarMenuItem
            key={item.id}
            item={item}
            variant="sidebar"
            isActive={isActive?.(item)}
            onItemClick={onItemClick}
          />
        ))}
      </nav>
    )
  }

  if (variant === 'mobile') {
    return (
      <nav className={cn('flex flex-col gap-4', className)}>
        {items.map((item) => (
          <MobileMenuItem
            key={item.id}
            item={item}
            variant="mobile"
            isActive={isActive?.(item)}
            onItemClick={onItemClick}
          />
        ))}
      </nav>
    )
  }

  return null
}

// Export icon utilities for reuse
export { DynamicIcon, getIcon }
